import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadExcel } from "@/lib/downloadExcel";
import { Card, StatCard, BarList, Empty } from "@/components/analytics/Primitives";
import { ClaimsPerDayChart, UpcomingChart, Funnel } from "@/components/analytics/Charts";
import { VehicleBreakdown } from "@/components/analytics/VehicleBreakdown";
import { StuckTable, RepeatTable, CitiesTable } from "@/components/analytics/Tables";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["12m", "12 months"], ["all", "All time"]];

const fmtRange = (d) => (d.start ? `${d.start} → ${d.end}` : `All claims through ${d.end}`);

export default function ClaimsAnalytics() {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const requestId = useRef(0);
  const load = async (r) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/claims/analytics`, { params: { range: r } });
      if (id !== requestId.current) return;
      setData(res.data);
    } catch (e) {
      if (id === requestId.current) toast.error("Failed to load analytics");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => { load(range); }, [range]);

  const openClaim = (claimNumber) => navigate(`/dashboard?claim=${claimNumber}`);
  const h = data?.headline;
  const ch = h?.change;

  return (
    <div className="dashboard-container min-h-screen">
      <div className="dashboard-header">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900" data-testid="analytics-title">Warranty Analytics</h1>
            <p className="text-sm text-gray-600 mt-1">{data ? fmtRange(data) : "Trends and breakdowns of incoming warranty claims"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => load(range)} disabled={loading} data-testid="analytics-refresh-button">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => downloadExcel(`${API}/claims/analytics/export?range=${range}`, `Hybrid2Go_Claims_Analytics_${range}.xlsx`)} data-testid="analytics-export-button">
              <Download className="w-4 h-4" /> Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/dashboard")} data-testid="back-to-claims-button">
              <ArrowLeft className="w-4 h-4" /> Back to Claims
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-6">
        <div className="inline-flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg" data-testid="range-tabs">
          {RANGES.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setRange(key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${range === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              data-testid={`range-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : !data ? null : (
          <div className={`space-y-6 transition-opacity ${loading ? "opacity-50" : ""}`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="headline-stats">
              <StatCard label="Total claims" value={h.total} change={ch ? ch.total : null} neutral testId="stat-total" />
              <StatCard label="Approval rate" value={`${h.approval_rate}%`} change={ch ? ch.approval_rate : null} changeSuffix=" pts" testId="stat-approval" />
              <StatCard label="Denial rate" value={`${h.denial_rate}%`} change={ch ? ch.denial_rate : null} changeSuffix=" pts" invert testId="stat-denial" />
              <StatCard label="Avg days to schedule" value={h.avg_days_to_schedule ?? "—"} change={ch ? ch.avg_days_to_schedule : null} changeSuffix=" days" invert testId="stat-days-to-schedule" />
            </div>

            <Card title="Claims per day" subtitle="New warranty claims submitted, Pacific time" testId="card-volume">
              <ClaimsPerDayChart volume={data.volume} />
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card title="Vehicle breakdown" subtitle="Tap a make for models, a model for years" testId="card-vehicles">
                <VehicleBreakdown vehicles={data.vehicles} />
              </Card>
              <div className="space-y-6">
                <Card title="Claims by state" testId="card-states">
                  {data.geography.states.length ? <BarList items={data.geography.states} testIdPrefix="state" /> : <Empty />}
                </Card>
                <Card title="Top cities" testId="card-cities">
                  <CitiesTable cities={data.geography.top_cities} />
                </Card>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card title="Status funnel" subtitle="Where claims from this period sit today" testId="card-funnel">
                <Funnel funnel={data.funnel} />
              </Card>
              <Card title="Turnaround" subtitle="How fast claims move from submission" testId="card-turnaround">
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="rounded-md bg-blue-50 p-3">
                    <p className="text-xs text-blue-700 font-semibold uppercase">Submitted → Scheduled</p>
                    <p className="text-2xl font-bold text-blue-800" data-testid="avg-days-schedule">{data.turnaround.avg_days_to_schedule ?? "—"}<span className="text-sm font-medium"> days</span></p>
                  </div>
                  <div className="rounded-md bg-indigo-50 p-3">
                    <p className="text-xs text-indigo-700 font-semibold uppercase">Submitted → Completed</p>
                    <p className="text-2xl font-bold text-indigo-800" data-testid="avg-days-complete">{data.turnaround.avg_days_to_complete ?? "—"}<span className="text-sm font-medium"> days</span></p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3" data-testid="not-closed-out">
                  {data.turnaround.not_closed_out} scheduled install{data.turnaround.not_closed_out === 1 ? "" : "s"} past their date but never marked Completed
                </p>
                <UpcomingChart upcoming={data.upcoming} />
                <p className="text-xs text-gray-500 mt-1 text-center">Scheduled installs, next 14 days</p>
              </Card>
            </div>

            <Card title={`Stuck claims (${data.turnaround.stuck.length})`} subtitle={`Open claims awaiting office action with no status change for ${data.turnaround.stuck_days_threshold}+ days — all time`} testId="card-stuck">
              <StuckTable stuck={data.turnaround.stuck} onOpen={openClaim} />
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card title="Common diagnostic codes" subtitle="Codes customers typed into the form" testId="card-codes">
                {data.diagnostic_codes.length ? <BarList items={data.diagnostic_codes} nameKey="code" testIdPrefix="code" /> : <Empty text="No diagnostic codes typed in this period (most customers upload photos instead)" />}
              </Card>
              <Card title={`Repeat customers (${data.repeat_customers.length})`} subtitle="2+ claims from the same phone or email in this period" testId="card-repeat">
                <RepeatTable rows={data.repeat_customers} onOpen={openClaim} />
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
